import React, { forwardRef, memo, useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { Theme } from 'theme';

import { Icon } from '../icon';
import { Body02 } from '../typography';
import { OverflowMenuContext } from './OverflowMenuContext';

export interface OverflowMenuItemProps {
    action: (event: React.FormEvent<HTMLElement>) => void;
    color?: string;
    disabled?: boolean;
    icon?: React.ReactNode;
    separated?: boolean;
    title: React.ReactNode | string;
    titleColor?: string;
}

interface MenuItemProps {
    item: OverflowMenuItemProps;
    hideShadow?: boolean;
}

const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>((props, ref) => {
    const { acceptFocus, isWhite } = useContext(OverflowMenuContext);
    const { item, hideShadow } = props;

    const theme = useContext(ThemeContext)!;

    const isReactElementContent = typeof item.title !== 'string';

    return (
        <Root isWhite={ isWhite }
            disabled={ item.disabled }
            onClick={ item.action }
            onMouseDown={ event => acceptFocus || event.preventDefault() } //this line prevents focus change on the menu item click
            separated={ item.separated }
            noPadding={ isReactElementContent }
            ref={ ref }
            hideShadow={ hideShadow }
            $y={ 0 }
        >
            { isReactElementContent ||
                <>
                    { item.icon && <StyledIcon color={ item.color }
                        icon={ item.icon }
                        size={ 16 }
                    /> }

                    <Body02 color={ item.disabled ? theme.semanticColor.textDisabled : item.titleColor ? item.titleColor : '' }
                    >
                        { item.title }
                    </Body02>
                </>
            }

            { isReactElementContent && item.title }
        </Root>
    );
});

MenuItem.displayName = 'OverflowMenuItem';

export default memo(MenuItem);

const getHoverItemBGColor = ({ isWhite, disabled, theme }: {
    disabled?: boolean;
    isWhite?: boolean;
    theme: Theme;
}) => {
    if (disabled) {
        return theme.color.grey.grey_0;
    }

    if (isWhite) {
        return theme.semanticColor.highlightPrimary;
    }

    return theme.color.grey.grey_10;
};

const shadowStyle = ({ separated, hideShadow }: {
    separated?: boolean;
    hideShadow?: boolean;
}) => {
    if (hideShadow) {
        return `box-shadow: none;`;
    }

    if (separated) {
        return css`box-shadow: ${ ({ theme }) => theme.shadow.shadow_i1_strong };`;
    }

    return css`box-shadow: ${ ({ theme }) => theme.shadow.shadow_i1 };`;
};

const Root = styled.div.attrs<{ $y: number }>(({ $y }) => ({
    style: {
        transform: `translateY(${ $y }px)`
    }
}))<{
    disabled?: boolean;
    noPadding: boolean;
    hideShadow?: boolean;
    separated?: boolean;
    isWhite?: boolean;
}>`
    align-items: center;
    position: relative;
    width: 100%;

    ${ shadowStyle }

    ${ ({ isWhite, disabled, noPadding, theme }) => `
        background-color: ${ isWhite ? theme.color.white : theme.semanticColor.ui_01 };
        cursor: ${ disabled ? 'not-allowed' : 'pointer' };
        display: flex;
        padding: ${ noPadding ? '0': theme.size.size_10 }
            ${ noPadding ? '': theme.size.size_40 }
            ${ noPadding ? '': theme.size.size_10 }
            ${ noPadding ? '': theme.size.size_20 };
        pointer-events: ${ disabled && 'none' };
        width: 100%;
    ` };

    @media (hover: hover) {
        &:hover {
            background-color: ${ getHoverItemBGColor };
        }
    }
`;

const StyledIcon = styled(Icon)`
  margin-right: ${ ({ theme }) => theme.size.size_10 };
`;
